import React, { useEffect, useState, useMemo } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { motion } from "framer-motion";
import {
  Container,
  Typography,
  Box,
  Card,
  CardContent,
  Button,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Snackbar,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Chip,
  Divider,
  Paper,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Alert,
  Stepper,
  Step,
  StepLabel,
} from "@mui/material";
import {
  ExpandMore,
  CalendarToday,
  School,
  TrendingUp,
  Assessment,
  ArrowBack,
  HistoryEdu,
  Person,
  Work,
  ContactMail,
  Download,
  Edit,
  Refresh,
} from "@mui/icons-material";
import axios from "axios";

const AlumniDetails = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { alumni } = location.state || {};
  
  const [alumniData, setAlumniData] = useState(alumni || null);
  const [results, setResults] = useState(null);
  const [loading, setLoading] = useState(true);
  const [academicRecords, setAcademicRecords] = useState([]);
  const [expandedYears, setExpandedYears] = useState({});
  const [showRevertModal, setShowRevertModal] = useState(false);
  const [revertStep, setRevertStep] = useState(0);
  const [revertData, setRevertData] = useState({
    reason: "",
    remarks: "",
    targetClass: "",
    targetStream: "",
  });
  const [reverting, setReverting] = useState(false);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });
  const [showEditModal, setShowEditModal] = useState(false);
  const [editData, setEditData] = useState({});
  const [editing, setEditing] = useState(false);

  const classes = [
    "PP1", "PP2", 
    "Grade 1", "Grade 2", "Grade 3", "Grade 4", "Grade 5", 
    "Grade 6", "Grade 7", "Grade 8", "Grade 9", 
    "Grade 10", "Grade 11", "Grade 12"
  ];

  useEffect(() => {
    const fetchAlumniDetails = async () => {
      if (!alumni?._id) {
        setSnackbar({
          open: true,
          message: "No alumni data provided",
          severity: "error",
        });
        navigate("/alumni");
        return;
      }

      try {
        setLoading(true);
        
        // Fetch alumni results and academic history
        const response = await axios.get(
          `https://ed-9ny1.onrender.com/api/schools/alumni/${alumni._id}/results`
        );

        if (response.data.success) {
          setAlumniData(response.data.data.alumni);
          setAcademicRecords(response.data.data.academicRecords);
          
          // Expand current/graduation year by default
          if (response.data.data.alumni.graduationYear) {
            setExpandedYears({ [response.data.data.alumni.graduationYear]: true });
          }
        }
      } catch (error) {
        console.error("Error fetching alumni details:", error);
        setSnackbar({
          open: true,
          message: "Failed to load alumni details",
          severity: "error",
        });
      } finally {
        setLoading(false);
      }
    };

    fetchAlumniDetails();
  }, [alumni?._id, navigate]);

  const handleRevertToLearner = async () => {
    if (!revertData.reason.trim()) {
      setSnackbar({
        open: true,
        message: "Please provide a reason for reverting",
        severity: "error",
      });
      return;
    }

    setReverting(true);
    try {
      const response = await axios.post(
        "https://ed-9ny1.onrender.com/api/schools/alumni/revert-to-learner",
        {
          alumniId: alumniData._id,
          reason: revertData.reason,
          remarks: revertData.remarks,
          targetClass: revertData.targetClass,
          targetStream: revertData.targetStream,
        }
      );

      if (response.data.success) {
        setSnackbar({
          open: true,
          message: response.data.message,
          severity: "success",
        });
        
        // Close modal and navigate to learners
        setShowRevertModal(false);
        setTimeout(() => {
          navigate("/all-students");
        }, 1500);
      }
    } catch (error) {
      console.error("Error reverting alumni:", error);
      setSnackbar({
        open: true,
        message: error.response?.data?.message || "Failed to revert alumni",
        severity: "error",
      });
    } finally {
      setReverting(false);
    }
  };

  const handleUpdateAlumni = async () => {
    setEditing(true);
    try {
      const response = await axios.put(
        `https://ed-9ny1.onrender.com/api/schools/alumni/${alumniData._id}`,
        editData
      );

      if (response.data.success) {
        setAlumniData(prev => ({ ...prev, ...editData }));
        setShowEditModal(false);
        setSnackbar({
          open: true,
          message: "Alumni information updated successfully",
          severity: "success",
        });
      }
    } catch (error) {
      console.error("Error updating alumni:", error);
      setSnackbar({
        open: true,
        message: error.response?.data?.message || "Failed to update alumni",
        severity: "error",
      });
    } finally {
      setEditing(false);
    }
  };

  const handleYearToggle = (year) => {
    setExpandedYears((prev) => ({
      ...prev,
      [year]: !prev[year],
    }));
  };

  const renderAcademicRecords = () => {
    if (!academicRecords || academicRecords.length === 0) {
      return (
        <Typography
          sx={{ color: "#666", fontStyle: "italic", p: 3, textAlign: "center" }}
        >
          No academic records available.
        </Typography>
      );
    }

    return academicRecords.map((record) => {
      const isGraduationYear = record.academicYear === alumniData?.graduationYear;
      const isExpanded = expandedYears[record.academicYear] || false;

      return (
        <Accordion
          key={`${record.academicYear}-${record.class}`}
          expanded={isExpanded}
          onChange={() => handleYearToggle(record.academicYear)}
          sx={{
            mb: 2,
            backgroundColor: isGraduationYear ? "#e8f5e9" : "#fafafa",
            border: "1px solid",
            borderColor: isGraduationYear ? "#4caf50" : "#e0e0e0",
            "&:before": { display: "none" },
          }}
        >
          <AccordionSummary
            expandIcon={<ExpandMore />}
            sx={{
              minHeight: "60px",
              "& .MuiAccordionSummary-content": {
                alignItems: "center",
                gap: 2,
              },
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
              <CalendarToday
                sx={{
                  color: isGraduationYear ? "#4caf50" : "#757575",
                  fontSize: "1.5rem",
                }}
              />
              <Box>
                <Typography
                  variant="h6"
                  sx={{
                    fontWeight: "bold",
                    color: isGraduationYear ? "#388e3c" : "#333",
                  }}
                >
                  Academic Year {record.academicYear}
                </Typography>
                <Typography
                  variant="body2"
                  sx={{
                    color: isGraduationYear ? "#388e3c" : "#666",
                    display: "flex",
                    alignItems: "center",
                    gap: 0.5,
                  }}
                >
                  <School fontSize="small" />
                  {record.class} {record.stream ? `• ${record.stream}` : ""}
                  {isGraduationYear && " (Graduation Year)"}
                </Typography>
              </Box>
            </Box>

            <Box
              sx={{ ml: "auto", display: "flex", alignItems: "center", gap: 1 }}
            >
              {isGraduationYear && (
                <Chip
                  label="Graduation Year"
                  size="small"
                  color="success"
                  variant="outlined"
                />
              )}
              {record.results && (
                <Chip
                  label={`${record.results.length} Results`}
                  size="small"
                  variant="outlined"
                />
              )}
            </Box>
          </AccordionSummary>

          <AccordionDetails sx={{ pt: 2, backgroundColor: "white" }}>
            {record.remarks && (
              <Typography
                variant="body2"
                sx={{ mb: 2, color: "#666", fontStyle: "italic" }}
              >
                {record.remarks}
              </Typography>
            )}

            {!record.results || record.results.length === 0 ? (
              <Typography
                sx={{
                  color: "#666",
                  fontStyle: "italic",
                  p: 3,
                  textAlign: "center",
                  border: "1px dashed #e0e0e0",
                  borderRadius: 1,
                }}
              >
                No results recorded for {record.academicYear}
              </Typography>
            ) : (
              <TableContainer component={Paper} variant="outlined">
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell><strong>Term</strong></TableCell>
                      <TableCell><strong>Exam Type</strong></TableCell>
                      <TableCell><strong>Subject</strong></TableCell>
                      <TableCell><strong>Marks</strong></TableCell>
                      <TableCell><strong>Grade</strong></TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {record.results.map((result, index) => (
                      <TableRow key={index}>
                        <TableCell>{result.term || "N/A"}</TableCell>
                        <TableCell>{result.examType || "N/A"}</TableCell>
                        <TableCell>{result.subject || "N/A"}</TableCell>
                        <TableCell>{result.marks || 0}</TableCell>
                        <TableCell>
                          {result.grade || (
                            result.marks >= 80 ? "A" :
                            result.marks >= 70 ? "B" :
                            result.marks >= 60 ? "C" :
                            result.marks >= 50 ? "D" : "E"
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </AccordionDetails>
        </Accordion>
      );
    });
  };

  const renderAcademicSummary = () => {
    if (academicRecords.length === 0) return null;

    return (
      <Paper
        elevation={0}
        sx={{
          p: 3,
          mb: 3,
          backgroundColor: "#f8f9fa",
          border: "1px solid #e0e0e0",
          borderRadius: 2,
        }}
      >
        <Typography
          variant="h6"
          sx={{
            fontWeight: "bold",
            color: "#333",
            mb: 2,
            display: "flex",
            alignItems: "center",
            gap: 1,
          }}
        >
          <HistoryEdu /> Academic Summary
        </Typography>

        <Grid container spacing={2}>
          <Grid item xs={12} md={3}>
            <Paper
              elevation={0}
              sx={{
                p: 2,
                backgroundColor: "white",
                border: "1px solid #e0e0e0",
                borderRadius: 2,
                textAlign: "center",
              }}
            >
              <Typography variant="body2" color="textSecondary">
                Total Academic Years
              </Typography>
              <Typography
                variant="h4"
                sx={{ color: "#388e3c", fontWeight: "bold" }}
              >
                {academicRecords.length}
              </Typography>
            </Paper>
          </Grid>

          <Grid item xs={12} md={9}>
            <Paper
              elevation={0}
              sx={{
                p: 2,
                backgroundColor: "white",
                border: "1px solid #e0e0e0",
                borderRadius: 2,
              }}
            >
              <Typography variant="body2" color="textSecondary" sx={{ mb: 1 }}>
                Academic Progression
              </Typography>
              <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
                {academicRecords.map((record) => (
                  <Chip
                    key={`${record.academicYear}-${record.class}`}
                    label={`${record.academicYear}: ${record.class}`}
                    color={
                      record.academicYear === alumniData?.graduationYear
                        ? "success"
                        : "default"
                    }
                    variant="outlined"
                    size="medium"
                    icon={<CalendarToday />}
                    sx={{ mb: 0.5 }}
                  />
                ))}
              </Box>
            </Paper>
          </Grid>
        </Grid>
      </Paper>
    );
  };

  const renderRevertStep = () => {
    switch (revertStep) {
      case 0:
        return (
          <Box>
            <Typography variant="h6" gutterBottom>
              Confirm Reversion
            </Typography>
            <Typography variant="body1" paragraph>
              You are about to revert <strong>{alumniData?.fullName}</strong> back to an active learner.
            </Typography>
            <Alert severity="info" sx={{ mb: 2 }}>
              This will:
              <ul>
                <li>Change status from "Alumni" to "Active"</li>
                <li>Move them back to the learners list</li>
                <li>Preserve all their academic history</li>
                <li>Allow them to continue with education</li>
              </ul>
            </Alert>
          </Box>
        );
      
      case 1:
        return (
          <Box>
            <Typography variant="h6" gutterBottom>
              Select Target Class
            </Typography>
            <Typography variant="body2" color="textSecondary" paragraph>
              Select which class to place the learner in after reversion.
            </Typography>
            
            <FormControl fullWidth sx={{ mt: 2 }}>
              <InputLabel>Target Class</InputLabel>
              <Select
                value={revertData.targetClass}
                onChange={(e) => setRevertData({
                  ...revertData,
                  targetClass: e.target.value
                })}
                label="Target Class"
              >
                <MenuItem value="">Select Class</MenuItem>
                {classes.map((cls) => (
                  <MenuItem key={cls} value={cls}>{cls}</MenuItem>
                ))}
              </Select>
            </FormControl>

            <TextField
              fullWidth
              label="Reason for Reversion"
              value={revertData.reason}
              onChange={(e) => setRevertData({
                ...revertData,
                reason: e.target.value
              })}
              margin="normal"
              required
              multiline
              rows={2}
              placeholder="E.g., Returning for additional studies, re-enrollment, etc."
            />

            <TextField
              fullWidth
              label="Additional Remarks (Optional)"
              value={revertData.remarks}
              onChange={(e) => setRevertData({
                ...revertData,
                remarks: e.target.value
              })}
              margin="normal"
              multiline
              rows={2}
            />
          </Box>
        );
      
      case 2:
        return (
          <Box>
            <Typography variant="h6" gutterBottom>
              Review Reversion Details
            </Typography>
            
            <TableContainer component={Paper} variant="outlined" sx={{ mb: 3 }}>
              <Table>
                <TableBody>
                  <TableRow>
                    <TableCell><strong>Alumni</strong></TableCell>
                    <TableCell>{alumniData?.fullName}</TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell><strong>Graduation Year</strong></TableCell>
                    <TableCell>{alumniData?.graduationYear}</TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell><strong>Graduating Class</strong></TableCell>
                    <TableCell>{alumniData?.graduatingClass}</TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell><strong>Target Class</strong></TableCell>
                    <TableCell>{revertData.targetClass}</TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell><strong>Reason</strong></TableCell>
                    <TableCell>{revertData.reason}</TableCell>
                  </TableRow>
                  {revertData.remarks && (
                    <TableRow>
                      <TableCell><strong>Remarks</strong></TableCell>
                      <TableCell>{revertData.remarks}</TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>

            <Alert severity="warning" sx={{ mb: 2 }}>
              This action cannot be undone. The alumni will become an active learner again.
            </Alert>
          </Box>
        );
      
      default:
        return null;
    }
  };

  if (loading) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "100vh",
        }}
      >
        <CircularProgress color="primary" />
      </Box>
    );
  }

  if (!alumniData) {
    return (
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Alert severity="error" sx={{ mb: 2 }}>
          No alumni data found
        </Alert>
        <Button
          variant="contained"
          onClick={() => navigate("/alumni")}
          startIcon={<ArrowBack />}
        >
          Back to Alumni
        </Button>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ py: 4, minHeight: "100vh" }}>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.5 }}
      >
        {/* Header */}
        <Paper
          elevation={0}
          sx={{
            p: 3,
            mb: 3,
            backgroundColor: "white",
            border: "1px solid #e0e0e0",
            borderRadius: 2,
          }}
        >
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "flex-start",
            }}
          >
            <Box>
              <Typography
                variant="h4"
                sx={{ fontWeight: "bold", color: "#333", mb: 1 }}
              >
                {alumniData.fullName}
                <Chip
                  label="Alumni"
                  color="success"
                  size="small"
                  sx={{ ml: 2, verticalAlign: 'middle' }}
                />
              </Typography>
              <Typography variant="subtitle1" sx={{ color: "#666", mb: 0.5 }}>
                Admission: {alumniData.admissionNumber} • Birth Cert: {alumniData.birthCertificateNumber}
              </Typography>
              <Typography
                variant="body2"
                sx={{ color: "#388e3c", fontWeight: "medium" }}
              >
                Graduated: {alumniData.graduationYear} • Class: {alumniData.graduatingClass}
                {alumniData.stream && ` • Stream: ${alumniData.stream}`}
              </Typography>
            </Box>

            <Box>
              <Chip
                label={`Class of ${alumniData.graduationYear}`}
                color="primary"
                variant="outlined"
                icon={<CalendarToday />}
                sx={{ fontWeight: "bold" }}
              />
            </Box>
          </Box>
        </Paper>

        {/* Academic Summary */}
        {renderAcademicSummary()}

        {/* Academic Results */}
        <Paper
          elevation={0}
          sx={{
            p: 3,
            mb: 3,
            backgroundColor: "white",
            border: "1px solid #e0e0e0",
            borderRadius: 2,
          }}
        >
          <Typography
            variant="h5"
            sx={{
              fontWeight: "bold",
              color: "#333",
              mb: 3,
              display: "flex",
              alignItems: "center",
              gap: 1,
            }}
          >
            <Assessment /> Academic History
          </Typography>

          {renderAcademicRecords()}
        </Paper>

        {/* Alumni Information */}
        <Paper
          elevation={0}
          sx={{
            p: 3,
            mb: 3,
            backgroundColor: "white",
            border: "1px solid #e0e0e0",
            borderRadius: 2,
          }}
        >
          <Typography
            variant="h6"
            sx={{ fontWeight: "bold", color: "#333", mb: 2 }}
          >
            Alumni Information
          </Typography>

          <Grid container spacing={2}>
            {/* Personal Info */}
            <Grid item xs={12} md={6}>
              <Card variant="outlined">
                <CardContent>
                  <Typography variant="subtitle2" color="textSecondary" gutterBottom>
                    <Person sx={{ verticalAlign: 'middle', mr: 1 }} />
                    Personal Information
                  </Typography>
                  <Typography variant="body2" sx={{ mb: 1 }}>
                    <strong>Date of Birth:</strong> {new Date(alumniData.dateOfBirth).toLocaleDateString()}
                  </Typography>
                  <Typography variant="body2" sx={{ mb: 1 }}>
                    <strong>Gender:</strong> {alumniData.gender === 'M' ? 'Male' : 'Female'}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>

            {/* Parent Info */}
            <Grid item xs={12} md={6}>
              <Card variant="outlined">
                <CardContent>
                  <Typography variant="subtitle2" color="textSecondary" gutterBottom>
                    <ContactMail sx={{ verticalAlign: 'middle', mr: 1 }} />
                    Parent Information
                  </Typography>
                  <Typography variant="body2" sx={{ mb: 1 }}>
                    <strong>Parent:</strong> {alumniData.parentName}
                  </Typography>
                  <Typography variant="body2" sx={{ mb: 1 }}>
                    <strong>Contact:</strong> {alumniData.parentContact}
                  </Typography>
                  <Typography variant="body2">
                    <strong>Email:</strong> {alumniData.parentEmail}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>

            {/* Further Education */}
            {alumniData.furtherEducation?.institution && (
              <Grid item xs={12} md={6}>
                <Card variant="outlined">
                  <CardContent>
                    <Typography variant="subtitle2" color="textSecondary" gutterBottom>
                      <School sx={{ verticalAlign: 'middle', mr: 1 }} />
                      Further Education
                    </Typography>
                    <Typography variant="body2" sx={{ mb: 1 }}>
                      <strong>Institution:</strong> {alumniData.furtherEducation.institution}
                    </Typography>
                    <Typography variant="body2" sx={{ mb: 1 }}>
                      <strong>Course:</strong> {alumniData.furtherEducation.course}
                    </Typography>
                    {alumniData.furtherEducation.yearStarted && (
                      <Typography variant="body2">
                        <strong>Year Started:</strong> {alumniData.furtherEducation.yearStarted}
                      </Typography>
                    )}
                  </CardContent>
                </Card>
              </Grid>
            )}

            {/* Current Occupation */}
            {alumniData.currentOccupation?.company && (
              <Grid item xs={12} md={6}>
                <Card variant="outlined">
                  <CardContent>
                    <Typography variant="subtitle2" color="textSecondary" gutterBottom>
                      <Work sx={{ verticalAlign: 'middle', mr: 1 }} />
                      Current Occupation
                    </Typography>
                    <Typography variant="body2" sx={{ mb: 1 }}>
                      <strong>Company:</strong> {alumniData.currentOccupation.company}
                    </Typography>
                    <Typography variant="body2" sx={{ mb: 1 }}>
                      <strong>Position:</strong> {alumniData.currentOccupation.position}
                    </Typography>
                    {alumniData.currentOccupation.industry && (
                      <Typography variant="body2">
                        <strong>Industry:</strong> {alumniData.currentOccupation.industry}
                      </Typography>
                    )}
                  </CardContent>
                </Card>
              </Grid>
            )}
          </Grid>
        </Paper>

        {/* Action Buttons */}
        <Grid container spacing={2} sx={{ mt: 3 }}>
          <Grid item xs={12} sm={6} md={4}>
            <Button
              fullWidth
              variant="contained"
              sx={{
                bgcolor: "#2196F3",
                "&:hover": { bgcolor: "#1E88E5" },
                py: 1.5,
              }}
              onClick={() => setShowEditModal(true)}
              startIcon={<Edit />}
            >
              Edit Alumni Info
            </Button>
          </Grid>

          <Grid item xs={12} sm={6} md={4}>
            <Button
              fullWidth
              variant="contained"
              sx={{
                bgcolor: "#4CAF50",
                "&:hover": { bgcolor: "#45A049" },
                py: 1.5,
              }}
              onClick={() => setShowRevertModal(true)}
              startIcon={<Refresh />}
            >
              Revert to Learner
            </Button>
          </Grid>

          <Grid item xs={12} sm={6} md={4}>
            <Button
              fullWidth
              variant="contained"
              sx={{
                bgcolor: "#607D8B",
                "&:hover": { bgcolor: "#455A64" },
                py: 1.5,
              }}
              onClick={() => navigate("/alumni")}
              startIcon={<ArrowBack />}
            >
              Back to Alumni
            </Button>
          </Grid>
        </Grid>

        {/* Revert to Learner Modal */}
        <Dialog
          open={showRevertModal}
          onClose={() => {
            if (!reverting) {
              setShowRevertModal(false);
              setRevertStep(0);
              setRevertData({
                reason: "",
                remarks: "",
                targetClass: "",
                targetStream: "",
              });
            }
          }}
          maxWidth="md"
          fullWidth
        >
          <DialogTitle>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <Refresh />
              Revert Alumni to Active Learner
            </Box>
          </DialogTitle>

          <DialogContent>
            <Stepper activeStep={revertStep} sx={{ mb: 3 }}>
              <Step>
                <StepLabel>Confirmation</StepLabel>
              </Step>
              <Step>
                <StepLabel>Details</StepLabel>
              </Step>
              <Step>
                <StepLabel>Review</StepLabel>
              </Step>
            </Stepper>

            {renderRevertStep()}
          </DialogContent>

          <DialogActions>
            <Button
              onClick={() => {
                if (revertStep > 0) {
                  setRevertStep(revertStep - 1);
                } else {
                  setShowRevertModal(false);
                  setRevertStep(0);
                }
              }}
              disabled={reverting}
            >
              {revertStep === 0 ? "Cancel" : "Back"}
            </Button>

            <Button
              onClick={() => {
                if (revertStep < 2) {
                  setRevertStep(revertStep + 1);
                } else {
                  handleRevertToLearner();
                }
              }}
              variant="contained"
              color="primary"
              disabled={reverting}
            >
              {revertStep === 2 ? (
                reverting ? (
                  <CircularProgress size={24} />
                ) : (
                  "Confirm Reversion"
                )
              ) : (
                "Next"
              )}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Edit Alumni Modal */}
        <Dialog
          open={showEditModal}
          onClose={() => setShowEditModal(false)}
          maxWidth="md"
          fullWidth
        >
          <DialogTitle>Edit Alumni Information</DialogTitle>
          <DialogContent>
            <Typography variant="subtitle1" sx={{ mt: 2, mb: 1 }}>
              Further Education
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Institution"
                  value={editData.furtherEducation?.institution || alumniData.furtherEducation?.institution || ""}
                  onChange={(e) => setEditData({
                    ...editData,
                    furtherEducation: {
                      ...editData.furtherEducation,
                      ...alumniData.furtherEducation,
                      institution: e.target.value
                    }
                  })}
                  margin="normal"
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Course"
                  value={editData.furtherEducation?.course || alumniData.furtherEducation?.course || ""}
                  onChange={(e) => setEditData({
                    ...editData,
                    furtherEducation: {
                      ...editData.furtherEducation,
                      ...alumniData.furtherEducation,
                      course: e.target.value
                    }
                  })}
                  margin="normal"
                />
              </Grid>
            </Grid>

            <Typography variant="subtitle1" sx={{ mt: 3, mb: 1 }}>
              Current Occupation
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Company"
                  value={editData.currentOccupation?.company || alumniData.currentOccupation?.company || ""}
                  onChange={(e) => setEditData({
                    ...editData,
                    currentOccupation: {
                      ...editData.currentOccupation,
                      ...alumniData.currentOccupation,
                      company: e.target.value
                    }
                  })}
                  margin="normal"
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Position"
                  value={editData.currentOccupation?.position || alumniData.currentOccupation?.position || ""}
                  onChange={(e) => setEditData({
                    ...editData,
                    currentOccupation: {
                      ...editData.currentOccupation,
                      ...alumniData.currentOccupation,
                      position: e.target.value
                    }
                  })}
                  margin="normal"
                />
              </Grid>
            </Grid>

            <Typography variant="subtitle1" sx={{ mt: 3, mb: 1 }}>
              Contact Information
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Email"
                  value={editData.contactInformation?.email || alumniData.contactInformation?.email || ""}
                  onChange={(e) => setEditData({
                    ...editData,
                    contactInformation: {
                      ...editData.contactInformation,
                      ...alumniData.contactInformation,
                      email: e.target.value
                    }
                  })}
                  margin="normal"
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Phone"
                  value={editData.contactInformation?.phone || alumniData.contactInformation?.phone || ""}
                  onChange={(e) => setEditData({
                    ...editData,
                    contactInformation: {
                      ...editData.contactInformation,
                      ...alumniData.contactInformation,
                      phone: e.target.value
                    }
                  })}
                  margin="normal"
                />
              </Grid>
            </Grid>

            <TextField
              fullWidth
              label="Remarks"
              value={editData.remarks || alumniData.remarks || ""}
              onChange={(e) => setEditData({
                ...editData,
                remarks: e.target.value
              })}
              margin="normal"
              multiline
              rows={3}
            />
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setShowEditModal(false)}>Cancel</Button>
            <Button
              onClick={handleUpdateAlumni}
              variant="contained"
              color="primary"
              disabled={editing}
            >
              {editing ? <CircularProgress size={24} /> : "Save Changes"}
            </Button>
          </DialogActions>
        </Dialog>

        <Snackbar
          open={snackbar.open}
          autoHideDuration={6000}
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          message={snackbar.message}
          sx={{
            bgcolor: snackbar.severity === "error" ? "#e74c3c" : 
                     snackbar.severity === "success" ? "#4CAF50" : "#FF9800",
          }}
        />
      </motion.div>
    </Container>
  );
};

export default AlumniDetails;